<script setup lang="ts">
import { computed, defineProps } from "vue";
import {
  ChineseCalendarAction,
  ChineseCalendarDate,
} from "@/types/calculators";
import { useI18n } from "vue-i18n";
import { WEEK_KEYS } from "@/constants/chineseCalendar";
import ChineseIcon from "@/views/Calculators/ChineseCalendar/components/ChineseIcon.vue";
import AppTooltip from "@/components/AppTooltip.vue";
import router from "@/router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import retrogradeIcon from "@/assets/icons/chineseCalendar/retrograde.svg";

const props = defineProps<{
  chineseCalendar: ChineseCalendarDate[];
  month: number;
  year: number;
  monthKey: string;
  showAsIcons: boolean;
}>();

const { t } = useI18n();

const firstDay = computed(() => {
  const startOfMonth = new Date(props.year, props.month, 1);
  const firstDayIndex = startOfMonth.getDay() - 1;
  // Sunday has 0 index that after -1 gives -1 index
  if (firstDayIndex == -1) {
    return 6;
  }
  return firstDayIndex;
});

const lastDay = computed(() => {
  const lastDay = new Date(props.year, props.month + 1, 0);
  return 7 - lastDay.getDay();
});

function openFullDayInfo(date: string) {
  router.push({
    name: ROUTE_NAMES.CHINESE_CALENDAR_DAY,
    query: { date: date },
  });
}
</script>

<template>
  <div class="grid grid-cols-[repeat(7,minmax(0,159px))] gap-2">
    <!-- WEEK'S NAMES -->
    <div
      v-for="weekKey in WEEK_KEYS"
      :key="weekKey"
      class="h-10 max-w-40 flex items-center justify-center bg-white rounded-lg"
    >
      <span class="font-semibold text-xl leading-[24px] text-brown">
        {{ t(`week-names.${weekKey}`) }}
      </span>
    </div>

    <!-- EMPTY CELLS BEFORE -->
    <div v-for="n in firstDay" :key="n" class="bg-[#E3E3E3] rounded-lg"></div>

    <!-- CHINESE CALENDAR -->
    <div
      v-for="(chineseDay, index) in chineseCalendar"
      :key="chineseDay.date"
      class="flex flex-col overflow-visible bg-white rounded-lg gap-4 px-2 pt-2 pb-4 text-left relative cursor-pointer"
      @click="openFullDayInfo(chineseDay.date)"
    >
      <!-- CHANGE SEASON BADGE -->
      <div
        v-if="chineseDay.season.new !== undefined"
        class="absolute w-[52px] h-6 bg-error-dark rounded-tr-xl rounded-bl-xl top-0 right-0 text-center"
      >
        <AppTooltip>
          <template #trigger>
            <span class="text-white text-xs">
              {{ chineseDay.season.change_time }}
            </span>
          </template>
          <template #popper>
            <p class="text-left whitespace-nowrap">
              {{
                t("calculators.chinese-calendar.season.end-tooltip", {
                  season: chineseDay.season.current,
                  time: chineseDay.season.change_time,
                })
              }}
            </p>
            <p class="text-left whitespace-nowrap">
              {{
                t("calculators.chinese-calendar.season.start-tooltip", {
                  season: chineseDay.season.new,
                })
              }}
            </p>
          </template>
        </AppTooltip>
      </div>
      <div
        class="flex lg:items-end gap-1"
        :class="{ 'mt-6 lg:mt-0': chineseDay.season.new !== undefined }"
      >
        <div class="flex flex-col gap-1 h-full">
          <AppTooltip>
            <template #trigger>
              <div
                class="size-5 bg-yellow-300 rounded-full text-xs text-brown flex items-center justify-center"
              >
                {{ chineseDay.moon_day }}
              </div>
            </template>
            <template #popper>
              {{
                t("calculators.chinese-calendar-day.day-summary.moon-day", {
                  moon_day: chineseDay.moon_day,
                })
              }}
            </template>
          </AppTooltip>
          <AppTooltip v-if="chineseDay.is_retrograde">
            <template #trigger>
              <img
                :src="retrogradeIcon"
                alt="retrograde day"
                class="size-4 mx-auto"
              />
            </template>
            <template #popper>
              {{
                t(
                  "calculators.chinese-calendar-day.day-summary.retrograde.content"
                )
              }}
            </template>
          </AppTooltip>
        </div>
        <span
          class="text-brown font-medium text-base lg:text-[40px] lg:leading-100p"
        >
          {{ index + 1 }}
        </span>
        <span class="text-muted text-sm max-lg:leading-[24px]">
          {{ t(`months.${monthKey}`) }}
        </span>
      </div>
      <div class="border border-muted rounded-lg p-2">
        <span class="font-bold text-xs break-all">
          {{ chineseDay.officer }}
        </span>
      </div>
      <div class="border border-success rounded-lg px-2 py-1">
        <span class="font-normal text-xs text-brown break-all">
          {{
            t("calculators.chinese-calendar.day-of", {
              animal: chineseDay.conform,
            })
          }}
        </span>
      </div>
      <div class="green-container" v-if="showAsIcons">
        <ChineseIcon
          v-for="action in chineseDay.actions.filter((a: ChineseCalendarAction) => a.is_positive)"
          :name="action.doing"
          :key="action.doing"
        />
      </div>
      <div class="green-container" v-else>
        <p class="text-sm text-brown text-left">
          {{
            chineseDay.actions
              .filter((a: ChineseCalendarAction) => a.is_positive)
              .map((a: ChineseCalendarAction) =>
                t(`calculators.chinese-calendar.doing.${a.doing}`)
              )
              .join(", ")
          }}
        </p>
      </div>
      <div class="border border-error-dark rounded-lg px-2 py-1">
        <span class="font-normal text-xs text-brown max-xl:break-all">
          {{
            t("calculators.chinese-calendar.no-conform", {
              animal: chineseDay.no_conform,
            })
          }}
        </span>
      </div>
      <div
        v-if="chineseDay.actions.filter((a: ChineseCalendarAction) => !a.is_positive).length > 0"
      >
        <div v-if="showAsIcons" class="red-container">
          <div
            v-for="action in chineseDay.actions.filter((a: ChineseCalendarAction) => !a.is_positive)"
            :key="action.doing"
          >
            <ChineseIcon
              v-if="action.doing !== 'not_suitable_for_important_matters'"
              :name="action.doing"
            />
            <span class="font-normal text-xs text-brown" v-else>
              {{
                t(
                  "calculators.chinese-calendar.doing.not_suitable_for_important_matters"
                )
              }}
            </span>
          </div>
        </div>
        <div v-else class="red-container">
          <p class="text-sm text-brown text-left">
            {{
              chineseDay.actions
                .filter((a: ChineseCalendarAction) => !a.is_positive)
                .map((a: ChineseCalendarAction) =>
                  t(`calculators.chinese-calendar.doing.${a.doing}`)
                )
                .join(", ")
            }}
          </p>
        </div>
      </div>
      <p class="text-muted text-xs">
        {{
          chineseDay.season.new !== undefined
            ? t("calculators.chinese-calendar.change-season")
            : chineseDay.season.current
        }}
      </p>
    </div>

    <!-- EMPTY CELLS AFTER -->
    <div v-for="n in lastDay" :key="n" class="bg-[#E3E3E3] rounded-lg"></div>
  </div>
</template>

<style scoped>
.green-container {
  @apply border border-success rounded-lg px-2 py-2 flex flex-wrap items-center justify-center gap-3;
}
.red-container {
  @apply border border-error-dark rounded-lg px-2 py-2 flex flex-wrap items-center justify-center gap-3;
}
</style>
